import React, { useState } from 'react';
import MetaTag from '@/components/ui/MetaTag';
import JsonLd from '@/components/ui/JsonLd';
import { BUSINESS } from '@/constants/business';
import { PROMOS, Promo, PromoImages } from './promo';
import { A, ArticleCard, Breadcrumb, Eyebrow, JournalShell, Section } from './components';
import { JournalArticle } from './const';

const SITE = BUSINESS.appUrl;
const APPOINTMENT = '/accounts/appointment/new/';
const articleUrl = (p: Promo) => `/article/${p.article}/`;
type Filter = 'all' | 'active' | 'ended';

export type JournalPromoProps = { images: PromoImages; news?: JournalArticle[]; promos?: Promo[] };

const Poster = ({ src, alt, className = '' }: { src?: string; alt: string; className?: string }) =>
  src ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt={alt} width={1080} height={1350} className={`tw-w-full tw-h-auto tw-aspect-[4/5] tw-object-cover ${className}`} />
  ) : (
    <div className={`tw-aspect-[4/5] tw-w-full tw-bg-jr-band ${className}`} />
  );

const Status = ({ children, light }: { children: React.ReactNode; light?: boolean }) => (
  <p className={`tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[12px] tw-font-medium tw-uppercase tw-tracking-[0.08em] ${light ? 'tw-text-[#cfe3de]' : 'tw-text-jr-green'}`}>
    <span aria-hidden="true" className={`tw-inline-block tw-h-[6px] tw-w-[6px] ${light ? 'tw-bg-[#cfe3de]' : 'tw-bg-jr-green'}`} />
    {children}
  </p>
);

const Title = ({ text, className = '' }: { text: string; className?: string }) => (
  <h2 className={`tw-font-jr tw-m-0 tw-whitespace-pre-line tw-text-[30px] md:tw-text-[44px] tw-font-normal tw-leading-[1.2] ${className}`}>{text}</h2>
);

export const JournalPromoLayout = ({ images, news = [], promos = PROMOS }: JournalPromoProps) => {
  const promoSlugs = new Set(promos.map((p) => p.article));
  const fresh = news.filter((a) => !promoSlugs.has(a.slug));
  const [filter, setFilter] = useState<Filter>('all');
  const live = promos.filter((p) => !p.ended);
  const past = promos.filter((p) => p.ended);
  const [featured, ...others] = live;
  const url = `${SITE}/tin-tuc/khuyen-mai/`;
  const title = 'Khuyến mãi An Thư: ưu đãi trang sức kim cương đang diễn ra';
  const description = 'Chương trình khuyến mãi, thời gian áp dụng và điều kiện tại An Thư The Diamond Store, TP.HCM.';
  const showLive = filter !== 'ended';
  const showPast = filter !== 'active';
  return (
    <JournalShell activeKey="khuyen-mai">
      <MetaTag title={title} description={description} image={images[featured?.slug] || `${SITE}/logo-bg.png`} />
      <JsonLd
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
              { '@type': 'ListItem', position: 2, name: 'Tin tức', item: `${SITE}/tin-tuc/` },
              { '@type': 'ListItem', position: 3, name: 'Khuyến mãi', item: url },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${url}#page`,
            url,
            name: title,
            description,
            inLanguage: 'vi',
            publisher: { '@id': BUSINESS.id },
            mainEntity: {
              '@type': 'OfferCatalog',
              name: 'Ưu đãi tại An Thư',
              itemListElement: live.map((p) => ({
                '@type': 'Offer',
                name: p.short,
                description: `${p.desc}${p.note ? ` ${p.note}` : ''}`,
                url: `${SITE}${articleUrl(p)}`,
                seller: { '@id': BUSINESS.id },
                ...(p.validFrom ? { validFrom: p.validFrom } : {}),
                ...(p.validThrough ? { validThrough: p.validThrough } : {}),
                ...(images[p.slug] ? { image: images[p.slug] } : {}),
              })),
            },
          },
        ]}
      />
      <Breadcrumb items={[{ label: 'Trang chủ', href: '/' }, { label: 'Tin tức', href: '/tin-tuc/' }, { label: 'Khuyến mãi' }]} />

      <section>
        <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-pb-8">
          <div className="tw-grid tw-gap-6 md:tw-grid-cols-[1.3fr_1fr] md:tw-items-end">
            <div>
              <Eyebrow>AN THƯ · ƯU ĐÃI</Eyebrow>
              <h1 className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[34px] md:tw-text-[52px] lg:tw-text-[56px] tw-font-normal tw-leading-[1.15]">
                <span className="tw-block">Đặc quyền cho</span>
                <span className="tw-block">điều xứng đáng.</span>
              </h1>
            </div>
            <p className="tw-m-0 tw-text-[17px] tw-text-jr-mute md:tw-max-w-[340px]">Khám phá chương trình đang áp dụng và chọn ưu đãi phù hợp cùng An Thư.</p>
          </div>
          <div className="tw-mt-8 tw-flex tw-flex-wrap tw-gap-3 tw-border-b tw-border-jr-line tw-pb-8" role="tablist" aria-label="Lọc chương trình">
            {(
              [
                ['all', 'Tất cả chương trình'],
                ['active', 'Đang diễn ra'],
                ['ended', 'Đã kết thúc'],
              ] as [Filter, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={filter === k}
                onClick={() => setFilter(k)}
                className={`tw-min-h-[44px] tw-px-6 tw-text-[14px] tw-font-medium ${filter === k ? 'tw-bg-jr-green tw-text-white' : 'tw-bg-jr-band tw-text-jr-ink hover:tw-bg-jr-top'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {showLive && featured && (
        <Section className="!tw-pt-0 !tw-pb-12">
          <Eyebrow className="tw-mb-6">CHƯƠNG TRÌNH NỔI BẬT</Eyebrow>
          <div className="tw-grid tw-gap-8 md:tw-gap-12 md:tw-grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] tw-items-center">
            <Poster src={images[featured.slug]} alt={featured.short} className="md:tw-max-w-[500px]" />
            <div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <Status>{featured.status}</Status>
                <p className="tw-m-0 tw-text-[13px] tw-text-jr-mute">{featured.period}</p>
              </div>
              <Title text={featured.title} className="tw-mt-6" />
              <p className="tw-mt-5 tw-mb-0 tw-text-[17px] tw-text-jr-mute tw-leading-relaxed">{featured.desc}</p>
              {featured.highlights && (
                <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-4">
                  {featured.highlights.map((h) => (
                    <div key={h.value} className="tw-bg-jr-band tw-p-5">
                      <p className="tw-font-jr tw-m-0 tw-text-[26px] tw-text-jr-green">{h.value}</p>
                      <p className="tw-mt-2 tw-mb-0 tw-text-[13px] tw-text-jr-mute">{h.label}</p>
                    </div>
                  ))}
                </div>
              )}
              {featured.note && <p className="tw-mt-5 tw-mb-0 tw-text-[14px] tw-text-jr-mute">{featured.note}</p>}
              <div className="tw-mt-7 tw-flex tw-flex-wrap tw-items-center tw-gap-x-8 tw-gap-y-3">
                <A href={articleUrl(featured)} className="tw-inline-flex tw-min-h-[48px] tw-items-center tw-bg-jr-green tw-px-7 tw-text-[15px] tw-font-medium tw-text-white hover:tw-opacity-90">
                  {featured.cta} →
                </A>
                <A href={APPOINTMENT} className="tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-underline">
                  Tư vấn cùng An Thư →
                </A>
              </div>
            </div>
          </div>
        </Section>
      )}

      {showLive &&
        others.map((p) => (
          <section key={p.slug} className="tw-bg-jr-green tw-text-white">
            <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-py-10 md:tw-py-14 tw-grid tw-gap-8 md:tw-gap-12 md:tw-grid-cols-[minmax(0,320px)_minmax(0,1fr)] tw-items-center">
              <Poster src={images[p.slug]} alt={p.short} />
              <div>
                <Status light>{p.status}</Status>
                <Title text={p.title} className="tw-mt-5 tw-text-white" />
                <p className="tw-mt-5 tw-mb-0 tw-max-w-[560px] tw-text-[17px] tw-leading-relaxed tw-text-[#e3efec]">{p.desc}</p>
                <p className="tw-mt-5 tw-mb-0 tw-text-[14px] tw-text-[#cfe3de]">{p.period}</p>
                <A href={articleUrl(p)} className="tw-mt-7 tw-inline-flex tw-min-h-[48px] tw-items-center tw-bg-jr-band tw-px-7 tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-opacity-90">
                  {p.cta} →
                </A>
              </div>
            </div>
          </section>
        ))}

      {showLive && fresh.length > 0 && (
        <Section>
          <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
            <div>
              <Eyebrow>MỚI NHẤT</Eyebrow>
              <h2 className="tw-font-jr tw-mt-3 tw-mb-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal">Tin khuyến mãi & sự kiện.</h2>
            </div>
            <p className="tw-m-0 tw-text-[15px] tw-text-jr-mute">Bài mới nhất từ An Thư, cập nhật tự động.</p>
          </div>
          <div className="tw-mt-8 tw-grid tw-gap-x-5 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
            {fresh.map((a) => (
              <ArticleCard key={a.id} a={a} fit="poster" showDate />
            ))}
          </div>
        </Section>
      )}

      {showPast && (
        <Section>
          <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
            <div>
              <Eyebrow>ĐÃ KẾT THÚC</Eyebrow>
              <h2 className="tw-font-jr tw-mt-3 tw-mb-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal">Những mùa ưu đãi trước.</h2>
            </div>
            <p className="tw-m-0 tw-text-[15px] tw-text-jr-mute">Thông tin lưu trữ để bạn xem lại chương trình.</p>
          </div>
          <div className="tw-mt-8 tw-grid tw-gap-4">
            {past.map((p) => (
              <div key={p.slug} className="tw-grid tw-gap-6 md:tw-grid-cols-[140px_1fr_auto] tw-items-center tw-bg-jr-band tw-p-5">
                <Poster src={images[p.slug]} alt={p.short} className="tw-max-w-[140px]" />
                <div>
                  <Status>{p.status}</Status>
                  <Title text={p.title.replace('\n', ' ')} className="tw-mt-3 !tw-text-[26px] md:!tw-text-[30px]" />
                  <p className="tw-mt-3 tw-mb-0 tw-text-[15px] tw-text-jr-mute">{p.desc}</p>
                </div>
                <A href={articleUrl(p)} className="tw-text-[15px] tw-text-jr-mute hover:tw-text-jr-green hover:tw-underline md:tw-self-end">
                  {p.cta} →
                </A>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section className="!tw-pt-4">
        <div className="tw-border-t tw-border-jr-line tw-pt-10">
          <h2 className="tw-font-jr tw-m-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal">Chọn ưu đãi phù hợp với bạn.</h2>
          <div className="tw-mt-8 tw-grid tw-gap-8 md:tw-grid-cols-3">
            {[
              ['Xem điều kiện', 'Đọc phạm vi và thời gian áp dụng của từng chương trình.'],
              ['Tư vấn cùng An Thư', 'Trao đổi về món trang sức và ưu đãi phù hợp với đơn hàng.'],
              ['Trải nghiệm tại cửa hàng', 'Xem sản phẩm và xác nhận điều kiện trước khi lựa chọn.'],
            ].map(([t, d], i) => (
              <div key={t}>
                <p className="tw-font-jr tw-m-0 tw-text-[24px] tw-text-jr-gold">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="tw-mt-3 tw-mb-0 tw-text-[18px] tw-font-medium">{t}</h3>
                <p className="tw-mt-2 tw-mb-0 tw-text-[15px] tw-text-jr-mute">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </JournalShell>
  );
};
